import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Alert,
  Snackbar,
  FormControlLabel,
  Switch,
  Breadcrumbs,
  Link,
  Grid,
} from '@mui/material';
import {
  ArrowBack,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIcon,
  LocationOn,
  Nfc,
  Bluetooth,
  SmartToy,
} from '@mui/icons-material';
import { api, endpoints } from '../config/api';
import { Tour, TourStop, Device, AudioFile, VideoFile, MapFragmentImage, VirtualWorld } from '../types';
import { Visibility } from '@mui/icons-material';

interface TourStats {
  totalViews: number;
  periodViews: number;
  dailyStats: { date: string; views: number }[];
}

const TourDetail: React.FC = () => {
  const { tourId } = useParams<{ tourId: string }>();
  const navigate = useNavigate();
  const [tour, setTour] = useState<Tour | null>(null);
  const [stops, setStops] = useState<TourStop[]>([]);
  const [devices, setDevices] = useState<Device[]>([]);
  const [audioFiles, setAudioFiles] = useState<AudioFile[]>([]);
  const [videoFiles, setVideoFiles] = useState<VideoFile[]>([]);
  const [mapImages, setMapImages] = useState<MapFragmentImage[]>([]);

  const [virtualWorlds, setVirtualWorlds] = useState<VirtualWorld[]>([]);
  const [stats, setStats] = useState<TourStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedStop, setSelectedStop] = useState<TourStop | null>(null);
  const [formData, setFormData] = useState<Partial<TourStop>>({
    deviceMacAddress: '',
    deviceName: '',
    order: 1,
    activationDistance: 2.0,
    title: '',
    description: '',
    audioFileId: null,
    mapFragmentImageId: null,
    videoFileId: null,
    virtualWorldId: null,
    showArAfterAudio: false,
    aiModeEnabled: false,
    aiModeDurationSeconds: 60,
    arDuration: 30,
    arBackgroundMusicId: null,
    longitude: null,
    latitude: null,
    nfcTagId1: null,
    nfcTagId2: null,
    isOptional: false,
  });

  useEffect(() => {
    if (tourId) {
      fetchData();
    }
  }, [tourId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [tourRes, devicesRes, audioRes, videoRes, mapRes, worldsRes, statsRes] = await Promise.all([
        api.get(endpoints.tour(tourId!)),
        api.get(endpoints.devices),
        api.get(endpoints.audioFiles),
        api.get(endpoints.videoFiles),
        api.get(endpoints.mapFragmentImages),
        api.get(endpoints.virtualWorlds),
        api.get(endpoints.tourStats(tourId!)).catch(err => ({ data: { data: null } })), // Non-blocking
      ]);

      // Il backend potrebbe restituire i dati direttamente in data, non data.data
      const tourData = tourRes.data.data || tourRes.data;
      setTour(tourData);
      setStops(tourData.tourStops || tourData.stops || []);
      setDevices(devicesRes.data.data || []);
      setAudioFiles(audioRes.data.data || []);
      setVideoFiles(videoRes.data.data || []);
      setMapImages(mapRes.data.data || []);
      const worlds = (worldsRes.data.data || []).map((world: any) => ({
        ...world,
        id: world.id || world._id // Map _id to id if id is missing
      }));
      setVirtualWorlds(worlds);

      if (statsRes.data && statsRes.data.data) {
        setStats(statsRes.data.data);
      }

      // Debug: log virtual worlds
      console.log('Virtual Worlds loaded:', worlds);
      console.log('Virtual Worlds count:', worlds.length);
      worlds.forEach((world: any, index: number) => {
        console.log(`World ${index}:`, {
          id: world.id,
          _id: world._id,
          name: world.name,
          description: world.description
        });
      });
    } catch (err) {
      setError('Impossibile recuperare i dettagli del tour');
      console.error('Errore caricamento tour:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateStop = () => {
    setSelectedStop(null);
    const nextOrder = stops.length > 0 ? Math.max(...stops.map(s => s.order)) + 1 : 1;
    setFormData({
      deviceMacAddress: '',
      deviceName: '',
      order: nextOrder,
      activationDistance: 2.0,
      title: '',
      description: '',
      audioFileId: null,
      mapFragmentImageId: null,
      videoFileId: null,
      virtualWorldId: null,
      showArAfterAudio: false,
      aiModeEnabled: false,
      aiModeDurationSeconds: 60,
      arDuration: 30,
      arBackgroundMusicId: null,
      longitude: null,
      latitude: null,
      nfcTagId1: null,
      nfcTagId2: null,
      isOptional: false,
    });
    setDialogOpen(true);
  };

  const handleEditStop = (stop: TourStop) => {
    setSelectedStop(stop);
    setFormData({
      deviceMacAddress: stop.deviceMacAddress,
      deviceName: stop.deviceName,
      order: stop.order,
      activationDistance: stop.activationDistance,
      title: stop.title,
      description: stop.description,
      audioFileId: stop.audioFileId,
      mapFragmentImageId: stop.mapFragmentImageId,
      videoFileId: stop.videoFileId,
      virtualWorldId: stop.virtualWorldId,
      showArAfterAudio: stop.showArAfterAudio,
      aiModeEnabled: stop.aiModeEnabled,
      aiModeDurationSeconds: stop.aiModeDurationSeconds,
      arDuration: stop.arDuration,
      arBackgroundMusicId: stop.arBackgroundMusicId,
      longitude: stop.longitude,
      latitude: stop.latitude,
      nfcTagId1: stop.nfcTagId1,
      nfcTagId2: stop.nfcTagId2,
      isOptional: stop.isOptional || false,
    });
    setDialogOpen(true);
  };

  const handleDeleteStop = (stop: TourStop) => {
    setSelectedStop(stop);
    setDeleteDialogOpen(true);
  };

  const handleSaveStop = async () => {
    try {
      // Clamp coordinates client-side as a guard
      const clamp = (val: number, min: number, max: number) => Math.min(max, Math.max(min, val));
      const lon = formData.longitude ?? null;
      const lat = formData.latitude ?? null;
      const aiDuration = Number(formData.aiModeDurationSeconds || 60);
      const stopData = {
        ...formData,
        aiModeDurationSeconds: Number.isFinite(aiDuration) ? Math.min(900, Math.max(15, aiDuration)) : 60,
        longitude: typeof lon === 'number' ? clamp(lon, -180, 180) : null,
        latitude: typeof lat === 'number' ? clamp(lat, -90, 90) : null,
        tourId: tourId,
      };

      if (selectedStop) {
        await api.put(endpoints.tourStop(tourId!, selectedStop.id), stopData);
        setSuccess('Tappa aggiornata con successo');
      } else {
        await api.post(endpoints.tourStops(tourId!), stopData);
        setSuccess('Tappa creata con successo');
      }
      setDialogOpen(false);
      fetchData();
    } catch (err) {
      const anyErr: any = err as any;
      const serverMsg = anyErr?.response?.data?.message;
      setError(serverMsg ? `Impossibile salvare la tappa: ${serverMsg}` : 'Impossibile salvare la tappa');
      console.error('Save stop error:', err);
    }
  };

  const confirmDeleteStop = async () => {
    if (selectedStop) {
      try {
        await api.delete(endpoints.tourStop(tourId!, selectedStop.id));
        setSuccess('Tappa eliminata con successo');
        setDeleteDialogOpen(false);
        fetchData();
      } catch (err) {
        setError('Impossibile eliminare la tappa');
        console.error(err);
      }
    }
  };

  const handleDeviceSelect = (macAddress: string) => {
    const device = devices.find(d => d.macAddress === macAddress);
    if (device) {
      setFormData({
        ...formData,
        deviceMacAddress: device.macAddress,
        deviceName: device.name,
        // Use nullish coalescing to keep 0 as valid value
        longitude: device.longitude ?? null,
        latitude: device.latitude ?? null,
      });
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography>Caricamento dettagli del tour...</Typography>
      </Box>
    );
  }

  if (!tour) {
    return (
      <Box>
        <Typography>Tour non trovato</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
        <Link
          underline="hover"
          color="inherit"
          href="#"
          onClick={(e) => {
            e.preventDefault();
            navigate('/tours');
          }}
        >
          Tour
        </Link>
        <Typography color="text.primary">{tour.name}</Typography>
      </Breadcrumbs>

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center">
          <IconButton onClick={() => navigate('/tours')} sx={{ mr: 2 }}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h4">{tour.name}</Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreateStop}>
          Aggiungi Tappa
        </Button>
      </Box>

      {tour.description && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="body1">{tour.description}</Typography>
        </Paper>
      )}

      {stats && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box display="flex" alignItems="center" mb={2}>
              <Visibility sx={{ mr: 1, color: 'text.secondary' }} />
              <Typography variant="h6">Statistiche Tour</Typography>
            </Box>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <Typography variant="overline" color="text.secondary">
                  Visualizzazioni Totali
                </Typography>
                <Typography variant="h4">{stats.totalViews}</Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <Typography variant="overline" color="text.secondary">
                  Ultimi 30 Giorni
                </Typography>
                <Typography variant="h4">{stats.periodViews}</Typography>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle2" gutterBottom sx={{ mt: 1 }}>
                  Andamento Giornaliero (Ultimi 30 giorni)
                </Typography>
                <Box sx={{ maxHeight: 200, overflowY: 'auto' }}>
                  <Grid container spacing={1}>
                    {stats.dailyStats.length > 0 ? (
                      stats.dailyStats.map((day) => (
                        <Grid size={{ xs: 6, sm: 4, md: 3 }} key={day.date}>
                          <Box
                            sx={{
                              p: 1,
                              border: '1px solid',
                              borderColor: 'divider',
                              borderRadius: 1,
                              textAlign: 'center',
                            }}
                          >
                            <Typography variant="caption" display="block" color="text.secondary">
                              {new Date(day.date).toLocaleDateString()}
                            </Typography>
                            <Typography variant="body2" fontWeight="bold">
                              {day.views} views
                            </Typography>
                          </Box>
                        </Grid>
                      ))
                    ) : (
                      <Grid size={{ xs: 12 }}>
                        <Typography variant="body2" color="text.secondary">
                          Nessuna visualizzazione negli ultimi 30 giorni.
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      <Typography variant="h6" gutterBottom>
        Tappe del Tour ({stops.length})
      </Typography>

      {stops.length === 0 ? (
        <Card>
          <CardContent>
            <Typography variant="body1" align="center">
              Nessuna tappa aggiunta. Aggiungi la prima tappa per costruire il tour.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <List>
          {stops.sort((a, b) => a.order - b.order).map((stop) => (
            <Paper key={stop.id} sx={{ mb: 2 }}>
              <ListItem>
                <Box sx={{ mr: 2 }}>
                  <Typography variant="h6">#{stop.order}</Typography>
                </Box>
                <ListItemText
                  primary={
                    <Box display="flex" alignItems="center" gap={1}>
                      {stop.title || 'Tappa senza titolo'}
                      {stop.longitude && stop.latitude && (
                        <Chip icon={<LocationOn />} label="GPS" size="small" />
                      )}
                      {(stop.nfcTagId1 || stop.nfcTagId2) && (
                        <Chip icon={<Nfc />} label="NFC" size="small" />
                      )}
                      {stop.aiModeEnabled && (
                        <Chip icon={<SmartToy />} label={`AI ${stop.aiModeDurationSeconds}s`} size="small" color="secondary" />
                      )}
                      <Chip icon={<Bluetooth />} label={stop.deviceName} size="small" />
                    </Box>
                  }
                  secondary={
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        {stop.description || 'Nessuna descrizione'}
                      </Typography>
                      <Box display="flex" gap={1} mt={1} flexWrap="wrap">
                        {stop.audioFileId && (
                          <Chip
                            label={
                              audioFiles.find(f => f.id === stop.audioFileId)?.originalName ||
                              `Audio (${stop.audioFileId.substring(0, 8)}...)`
                            }
                            size="small"
                            variant="outlined"
                            title="Audio file"
                          />
                        )}
                        {stop.videoFileId && (
                          <Chip
                            label={
                              videoFiles.find(f => f.id === stop.videoFileId)?.originalName ||
                              `Video (${stop.videoFileId.substring(0, 8)}...)`
                            }
                            size="small"
                            variant="outlined"
                            title="Video file"
                          />
                        )}
                        {stop.mapFragmentImageId && (
                          <Chip
                            label={
                              mapImages.find(f => f.id === stop.mapFragmentImageId)?.originalName ||
                              `Map (${stop.mapFragmentImageId.substring(0, 8)}...)`
                            }
                            size="small"
                            variant="outlined"
                            title="Map image"
                          />
                        )}
                        {stop.virtualWorldId && (
                          <Chip
                            label={
                              stop.virtualWorldId === 'AR_PORTAL_TEMPLATE'
                                ? '🌀 AR Portal Template'
                                : virtualWorlds.find(w => w.id === stop.virtualWorldId)?.name ||
                                `AR World (${stop.virtualWorldId.substring(0, 8)}...)`
                            }
                            size="small"
                            variant="outlined"
                            title="Virtual world"
                            sx={stop.virtualWorldId === 'AR_PORTAL_TEMPLATE' ? { backgroundColor: 'action.hover' } : {}}
                          />
                        )}
                      </Box>
                    </Box>
                  }
                />
                <ListItemSecondaryAction>
                  <IconButton onClick={() => handleEditStop(stop)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton onClick={() => handleDeleteStop(stop)}>
                    <DeleteIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            </Paper>
          ))}
        </List>
      )}

      {/* Create/Edit Stop Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{selectedStop ? 'Modifica Tappa' : 'Aggiungi Tappa'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Dispositivo</InputLabel>
                <Select
                  value={formData.deviceMacAddress || ''}
                  label="Dispositivo"
                  onChange={(e) => handleDeviceSelect(e.target.value)}
                >
                  {devices
                    .filter((device) => {
                      // If editing, allow the currently selected device
                      if (selectedStop && device.macAddress === selectedStop.deviceMacAddress) {
                        return true;
                      }
                      // Otherwise, filter out devices already used in other stops
                      return !stops.some(stop =>
                        stop.id !== selectedStop?.id &&
                        stop.deviceMacAddress === device.macAddress
                      );
                    })
                    .map((device) => (
                      <MenuItem key={device.macAddress} value={device.macAddress}>
                        {device.name} ({device.macAddress})
                      </MenuItem>
                    ))}
                  {devices.filter((device) => {
                    // Show unavailable devices as disabled options for clarity
                    if (selectedStop && device.macAddress === selectedStop.deviceMacAddress) {
                      return false; // Don't show as disabled if it's the current device
                    }
                    return stops.some(stop =>
                      stop.id !== selectedStop?.id &&
                      stop.deviceMacAddress === device.macAddress
                    );
                  }).length > 0 && (
                      <>
                        <MenuItem disabled>
                          <Typography variant="caption" color="text.secondary">
                            — Già Utilizzati in Questo Tour —
                          </Typography>
                        </MenuItem>
                        {devices.filter((device) => {
                          if (selectedStop && device.macAddress === selectedStop.deviceMacAddress) {
                            return false;
                          }
                          return stops.some(stop =>
                            stop.id !== selectedStop?.id &&
                            stop.deviceMacAddress === device.macAddress
                          );
                        }).map((device) => {
                          const usedInStop = stops.find(s => s.deviceMacAddress === device.macAddress);
                          return (
                            <MenuItem key={device.macAddress} value={device.macAddress} disabled>
                              <Box>
                                <Typography variant="body2" color="text.disabled">
                                  {device.name} ({device.macAddress})
                                </Typography>
                                <Typography variant="caption" color="text.disabled">
                                  Usato nella tappa #{usedInStop?.order}: {usedInStop?.title || 'Senza titolo'}
                                </Typography>
                              </Box>
                            </MenuItem>
                          );
                        })}
                      </>
                    )}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                label="Ordine"
                type="number"
                value={formData.order}
                onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                label="Distanza Attivazione (m)"
                type="number"
                value={formData.activationDistance}
                onChange={(e) => setFormData({ ...formData, activationDistance: parseFloat(e.target.value) })}
              />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth
                label="Titolo"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth
                label="Descrizione"
                multiline
                rows={3}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth>
                <InputLabel>File Audio</InputLabel>
                <Select
                  value={formData.audioFileId || ''}
                  label="File Audio"
                  onChange={(e) => setFormData({ ...formData, audioFileId: e.target.value || null })}
                >
                  <MenuItem value="">Nessuno</MenuItem>
                  {audioFiles.map((file) => (
                    <MenuItem key={file.id} value={file.id}>
                      {file.title || file.originalName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth>
                <InputLabel>File Video</InputLabel>
                <Select
                  value={formData.videoFileId || ''}
                  label="File Video"
                  onChange={(e) => setFormData({ ...formData, videoFileId: e.target.value || null })}
                >
                  <MenuItem value="">Nessuno</MenuItem>
                  {videoFiles.map((file) => (
                    <MenuItem key={file.id} value={file.id}>
                      {file.title || file.originalName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Immagine Mappa</InputLabel>
                <Select
                  value={formData.mapFragmentImageId || ''}
                  label="Immagine Mappa"
                  onChange={(e) => setFormData({ ...formData, mapFragmentImageId: e.target.value || null })}
                >
                  <MenuItem value="">Nessuna</MenuItem>
                  {mapImages.map((file) => (
                    <MenuItem key={file.id} value={file.id}>
                      {file.title || file.originalName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Mondo Virtuale</InputLabel>
                <Select
                  value={formData.virtualWorldId || ''}
                  label="Mondo Virtuale"
                  onChange={(e) => {
                    console.log('Virtual World selected:', e.target.value);
                    setFormData({ ...formData, virtualWorldId: e.target.value || null });
                  }}
                >
                  <MenuItem value="">Nessuno</MenuItem>
                  <MenuItem value="AR_PORTAL_TEMPLATE">
                    🌀 AR Portal Template
                  </MenuItem>
                  {virtualWorlds.map((world, index) => {
                    console.log(`Rendering MenuItem for world ${index}:`, { id: world.id, name: world.name });
                    return (
                      <MenuItem key={world.id || index} value={world.id}>
                        {world.name}{world.description ? ` - ${world.description}` : ''}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Longitudine"
                type="number"
                value={formData.longitude || ''}
                onChange={(e) => setFormData({ ...formData, longitude: e.target.value ? parseFloat(e.target.value) : null })}
                inputProps={{ min: -180, max: 180, step: 0.000001 }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Latitudine"
                type="number"
                value={formData.latitude || ''}
                onChange={(e) => setFormData({ ...formData, latitude: e.target.value ? parseFloat(e.target.value) : null })}
                inputProps={{ min: -90, max: 90, step: 0.000001 }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="NFC Tag ID 1"
                value={formData.nfcTagId1 || ''}
                onChange={(e) => setFormData({ ...formData, nfcTagId1: e.target.value || null })}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="NFC Tag ID 2"
                value={formData.nfcTagId2 || ''}
                onChange={(e) => setFormData({ ...formData, nfcTagId2: e.target.value || null })}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.aiModeEnabled || false}
                    onChange={(e) => setFormData({ ...formData, aiModeEnabled: e.target.checked })}
                  />
                }
                label="Abilita modalita AI dopo l'audio"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Durata modalita AI (secondi)"
                type="number"
                value={formData.aiModeDurationSeconds ?? 60}
                disabled={!formData.aiModeEnabled}
                onChange={(e) => setFormData({ ...formData, aiModeDurationSeconds: parseInt(e.target.value || '60', 10) })}
                InputProps={{ inputProps: { min: 15, max: 900 } }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.showArAfterAudio}
                    onChange={(e) => setFormData({ ...formData, showArAfterAudio: e.target.checked })}
                  />
                }
                label="Mostra AR dopo Audio"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isOptional || false}
                    onChange={(e) => setFormData({ ...formData, isOptional: e.target.checked })}
                  />
                }
                label="Tappa Opzionale (non blocca le successive)"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Durata AR (secondi)"
                type="number"
                value={formData.arDuration}
                onChange={(e) => setFormData({ ...formData, arDuration: parseInt(e.target.value) })}
                InputProps={{ inputProps: { min: 10, max: 300 } }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Musica Background AR</InputLabel>
                <Select
                  value={formData.arBackgroundMusicId || ''}
                  label="Musica Background AR"
                  onChange={(e) => setFormData({ ...formData, arBackgroundMusicId: e.target.value || null })}
                >
                  <MenuItem value="">Nessuna</MenuItem>
                  {audioFiles.map((file) => (
                    <MenuItem key={file.id} value={file.id}>
                      {file.title || file.originalName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Annulla</Button>
          <Button
            onClick={handleSaveStop}
            variant="contained"
            disabled={!formData.deviceMacAddress || !formData.deviceName}
          >
            {selectedStop ? 'Aggiorna' : 'Crea'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Conferma Eliminazione</DialogTitle>
        <DialogContent>
          <Typography>
            Sei sicuro di voler eliminare la tappa #{selectedStop?.order} "{selectedStop?.title || 'Senza titolo'}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Annulla</Button>
          <Button onClick={confirmDeleteStop} color="error" variant="contained">
            Elimina
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notifications */}
      <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError('')}>
        <Alert severity="error" onClose={() => setError('')}>
          {error}
        </Alert>
      </Snackbar>
      <Snackbar open={!!success} autoHideDuration={4000} onClose={() => setSuccess('')}>
        <Alert severity="success" onClose={() => setSuccess('')}>
          {success}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TourDetail;
